import { useApi } from '@hooks/useApi';
import { Dashboard } from '@protos/v2/dashboard';
import { useCallback, useEffect, useState } from 'react';
import { useAsync } from 'react-use';
import { UserSettings } from '@shared/protos/user';

export const useDashboardOrder = (allDashboards: Dashboard[]) => {
  const { apiClient } = useApi();
  const [sortedDashboardList, setSortedDashboardList] = useState<Dashboard[]>([]);
  const [dashboardOrder, setDashboardOrder] = useState<number[]>([]);

  const userSettings = useAsync(async () => {
    if (!apiClient) return null;
    return await apiClient.getUserSettings();
  }, [apiClient]);

  useEffect(() => {
    if (!userSettings.loading && userSettings.value) {
      const serverOrder: number[] | undefined = userSettings.value.dashboard?.dashboardsOrder;
      if (serverOrder) {
        setDashboardOrder(serverOrder);
      }
    }
  }, [userSettings]);

  useEffect(() => {
    const dashboardMap = new Map(allDashboards.map(d => [d.id, d]));

    const orderedDashboards: Dashboard[] = [];
    dashboardOrder.forEach(id => {
      const dashboard = dashboardMap.get(id);
      if (dashboard) {
        orderedDashboards.push(dashboard);
        dashboardMap.delete(id);
      }
    });

    dashboardMap.forEach(dashboard => {
      orderedDashboards.push(dashboard);
    });

    setSortedDashboardList(orderedDashboards);
  }, [dashboardOrder, allDashboards]);

  const saveOrderToServer = useCallback(
    async (newOrderIds: number[]) => {
      if (!apiClient) return;

      try {
        const currentSettings = await apiClient.getUserSettings();
        const settings = currentSettings || { dashboard: {}, user: {}, ocl_trading: {} };
        const updatedSettings: UserSettings = {
          ...settings,
          dashboard: {
            ...settings.dashboard,
            dashboardsOrder: newOrderIds,
          },
        };
        await apiClient.updateUserSettings(updatedSettings);
      } catch (error) {
        console.error('Error saving dashboard order to server', error);
      }
    },
    [apiClient]
  );

  const updateDashboardOrder = useCallback(
    (newOrder: Dashboard[]) => {
      const newOrderIds = newOrder.map(d => d.id);

      setSortedDashboardList(newOrder);
      setDashboardOrder(newOrderIds);
      saveOrderToServer(newOrderIds);
    },
    [saveOrderToServer]
  );

  const addNewDashboardToOrder = useCallback(
    (newDashboard: Dashboard) => {
      setSortedDashboardList(currentList => {
        const newOrder = currentList.find(d => d.id === newDashboard.id) ? currentList : [...currentList, newDashboard];
        const newOrderIds = newOrder.map(d => d.id);
        setDashboardOrder(newOrderIds);
        saveOrderToServer(newOrderIds);

        return newOrder;
      });
    },
    [saveOrderToServer]
  );

  const removeDashboardFromOrder = useCallback(
    (dashboardId: number) => {
      setSortedDashboardList(currentList => {
        const newOrder = currentList.filter(d => d.id !== dashboardId);
        const newOrderIds = newOrder.map(d => d.id);

        setDashboardOrder(newOrderIds);
        saveOrderToServer(newOrderIds);

        return newOrder;
      });
    },
    [saveOrderToServer]
  );

  return {
    sortedDashboardList,
    updateDashboardOrder,
    addNewDashboardToOrder,
    removeDashboardFromOrder,
    loading: userSettings.loading,
  };
};
